import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../../firebase";
import { Icons } from "../../lib/Icons";

/**
 * Auth modal for the solo landing page: login form with "Create an account" at the bottom.
 * Opens by default in login mode; successful login closes the modal (parent re-renders with user).
 */
const SoloAuthModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetDisabledUntil, setResetDisabledUntil] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);

      const functions = getFunctions(undefined, "europe-west3");
      const checkSubscription = httpsCallable(functions, "checkSoloSubscriptionFunc");
      const result = await checkSubscription();

      if (result.data.status === "none" && !result.data.userType) {
        try {
          const createSoloUser = httpsCallable(functions, "createSoloUserFunc");
          await createSoloUser({
            email,
            displayName: email.split("@")[0],
          });
        } catch (profileError) {
          // Profile might already exist
        }
      }

      onClose();

      if (result.data.hasActiveSubscription || result.data.isBetaTester) {
        // Stay on dashboard; auth state will update
        return;
      }
      navigate(`/solo/checkout`);
    } catch (err) {
      if (
        err.code === "auth/user-not-found" ||
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-credential"
      ) {
        setError("Invalid email or password. Please check your details and try again.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please try again later or reset your password.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Enter your email above, then click "Forgot password?"');
      return;
    }
    try {
      const functions = getFunctions(undefined, "europe-west3");
      const generateResetLink = httpsCallable(functions, "generatePasswordResetLinkFunc");
      await generateResetLink({ email });
      setResetDisabledUntil(Date.now() + 30_000);
      setError("");
      alert("Password reset email sent! Check your inbox.");
    } catch (e) {
      setResetDisabledUntil(Date.now() + 30_000);
      setError("");
      alert("If an account exists with this email, you'll receive a password reset link.");
    }
  };

  const canSendReset = Date.now() >= resetDisabledUntil;
  const resetSecondsLeft = Math.max(0, Math.ceil((resetDisabledUntil - Date.now()) / 1000));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 px-6 py-6 text-center">
          <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center mb-3">
            <Icons.Lock className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">Log in</h2>
          <p className="text-purple-100 text-sm mt-1">Sign in to access your SEN resources</p>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 rounded-xl border border-red-400/30">
              <p className="text-red-100 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="auth-modal-email" className="block text-sm font-medium text-slate-300 mb-1">
                Email
              </label>
              <input
                id="auth-modal-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full rounded-xl border border-slate-600 bg-slate-700/50 px-4 py-3 text-white placeholder-slate-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/50 focus:outline-none text-sm"
              />
            </div>
            <div>
              <label htmlFor="auth-modal-password" className="block text-sm font-medium text-slate-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="auth-modal-password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="block w-full rounded-xl border border-slate-600 bg-slate-700/50 px-4 py-3 pr-11 text-white placeholder-slate-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/50 focus:outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white transition-colors rounded"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <Icons.EyeSlash className="w-5 h-5" /> : <Icons.Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={canSendReset ? handlePasswordReset : undefined}
                disabled={!canSendReset}
                className="text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-50"
              >
                {canSendReset ? "Forgot password?" : `Reset link sent (${resetSecondsLeft}s)`}
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-3 text-white font-semibold text-sm hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <p className="mt-5 pt-4 border-t border-slate-700 text-center text-sm text-slate-400">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/signup/solo");
              }}
              className="font-medium text-purple-300 hover:text-white transition-colors"
            >
              Create an account
            </button>
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Close"
        >
          <Icons.XMark className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default SoloAuthModal;
